import React, { useState } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import "./EmailSignIn.css"; // Ensure this is properly linked to apply your styles
import Input from "../components/Input";
import { FormRow } from "../pages/Common/LandingPage/SectionContent/AboutContent";
import styled from "@emotion/styled";
import Button from "../components/Button/Button";
import { useTheme } from "@emotion/react";
import { useUserControls } from "../context/UserControlsProvider";

const EmailSignInWrapper = styled.div`
  width: 70%;
`;

interface EmailSignInProps {
  onSuccessfulLogin: () => void;
}

const EmailSignIn: React.FC<EmailSignInProps> = ({ onSuccessfulLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showSnackbar } = useUserControls();

  const auth = getAuth();

  const handleSignIn = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onSuccessfulLogin();
    } catch (error: any) {
      console.error("Error signing in with email: ", error);
      const code = (error?.code || "").toString();
      let message = "Unable to sign in. Please check your email and password.";
      if (code.includes("auth/wrong-password") || code.includes("wrong-password")) {
        message = "Incorrect password. Please try again.";
      } else if (code.includes("auth/user-not-found")) {
        message = "No account found for that email.";
      } else if (code.includes("auth/invalid-email")) {
        message = "Invalid email format.";
      } else if (code.includes("too-many-requests")) {
        message = "Too many attempts. Please try again later.";
      }
      showSnackbar({ message, severity: "error", duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      onSuccessfulLogin();
    } catch (error: any) {
      console.error("Error creating user: ", error);
      const code = (error?.code || "").toString();
      let message = "Unable to create account.";
      if (code.includes("auth/email-already-in-use")) {
        message = "Email already in use.";
      } else if (code.includes("auth/invalid-email")) {
        message = "Invalid email format.";
      } else if (code.includes("auth/weak-password")) {
        message = "Password is too weak.";
      }
      showSnackbar({ message, severity: "error", duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const theme: any = useTheme();

  return (
    <EmailSignInWrapper>
      <FormRow labelWidth="100%">
        <label>
          Email:
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
      </FormRow>
      <FormRow labelWidth="100%">
        <label>
          Password:
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
      </FormRow>
      {isSignUp ? (
        <>
          <div className="email-signin-button smaller" onClick={handleSignUp}>
            Request Access
          </div>
          <p>
            Already have an account?{" "}
            <span onClick={() => setIsSignUp(false)}>Sign In</span>
          </p>
        </>
      ) : (
        <>
          <FormRow labelWidth="100%">
            <Button
              size="md"
              onClick={handleSignIn}
              style={{
                width: "100%",
                margin: "1rem .5rem 0 .5rem",
                padding: "1rem",
                backgroundColor: theme.brand,
              }}
              disabled={submitting || !email || !password}
            >
              Sign In
            </Button>
          </FormRow>
          {/* Commenting out the Sign Up prompt for temporary removal */}
          {/*
          <p>
            Don't have an account? <span onClick={() => setIsSignUp(true)}>Sign Up</span>
          </p>
          */}
        </>
      )}
    </EmailSignInWrapper>
  );
};

export default EmailSignIn;
